import React from 'react';
import { Manufacturer } from '../../services/api/domains/manufacturers/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  manufacturerIdField,
  manufacturerAlwaysDisabledFields,
  getManufacturerFields,
} from './configs/manufacturerFields';
import { Facility } from 'services/api/domains/facilities/types';

interface ManufacturerModalProps {
  manufacturer: Manufacturer;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedManufacturer: Manufacturer) => void;
  close: () => void;
  allFacilities: Facility[];
}

const ManufacturerModal: React.FC<ManufacturerModalProps> = ({
  manufacturer,
  initialMode = 'VIEW',
  save,
  close,
  allFacilities,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedManufacturer,
    setEditedEntity: setEditedManufacturer,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: manufacturer,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: manufacturerAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'EDIT'
              ? 'Edit Manufacturer'
              : 'Manufacturer Details'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {/* Manufacturer ID field if present */}
          {manufacturer.manufacturer_id && (
            <ModalFieldRenderer
              fields={[manufacturerIdField]}
              entity={editedManufacturer}
              setEntity={setEditedManufacturer}
              mode={modalMode}
              focusedField={focusedField}
              setFocusedField={setFocusedField}
              isFieldDisabled={isFieldDisabled}
              getFloatingLabelStyle={getFloatingLabelStyle}
              getFloatingInputStyle={getFloatingInputStyle}
              styles={styles}
              missingMessage="Unknown"
            />
          )}

          {/* All manufacturer fields */}
          <ModalFieldRenderer
            fields={getManufacturerFields({
              facilities: allFacilities,
            })}
            entity={editedManufacturer}
            setEntity={setEditedManufacturer}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ManufacturerModal);
export type { ModalMode };
